import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  ImageHeader,
  MetaApprovalStatusDefinition,
  TextHeader,
  WhatsAppContentDefinition,
} from 'modules/graphql/types';
import { getIsEditMode, SettingsReadOnlyText } from 'modules/templateEditor/common';
import { WhatsAppRestrictionText } from 'modules/templateEditor/common/RestrictionText';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { ReadOnlyField } from 'modules/templateEditor/EditEmbedded/utils';
import { ButtonGroup, GroupButton } from 'ui/buttonGroup';
import { FormField } from 'ui/formField';
import { NegativeFill } from 'ui/icons';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { UrlInputField } from '../../common/components';
import { displayContent } from '../../common/content';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { FieldNames, MediumDataKey } from '../../common/types';
import { constructFieldErrors } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { BI_IMAGE_HEADER_EDITABLE_STATUSES, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';
import { InputTextField, MAX_HEADER_TEXT_LENGTH } from './components/InputTextField';

const header: MediumDataKey = 'header';
const headerMedia: MediumDataKey = 'header.mediaUrl';

const StyledFormField = styled(FormField)`
  margin-top: ${spacing.plusHalf};
`;

const StyledGroupButton = styled(GroupButton)`
  width: 100%;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  margin-bottom: ${spacing.threeQuarter};

  > div {
    width: 100%;
  }
`;

const StyledNegativeFill = styled(NegativeFill)`
  min-width: ${pxToRem(16)};
  min-height: ${pxToRem(16)};
`;

enum HeaderType {
  None = 'None',
  Text = 'Text',
  Image = 'Image',
}

const getHelpText = (isEditMode: boolean, headerType: HeaderType) => {
  if (isEditMode) {
    if (headerType === HeaderType.Text) {
      return 'Can contain one merge parameter';
    } else if (headerType === HeaderType.Image) {
      return 'Supports JPEG or PNG less than 16MB and a recommended resolution of at least 640x360 pixels for optimal display.';
    }
  }
  return null;
};

type Props = { isReviewTemplateTakeover?: boolean };

export const HeaderField = ({ isReviewTemplateTakeover = false }: Props) => {
  const { isEditMode, liveTemplate, setCurrentMediumField, initialTemplate } =
    useEditableFieldsContext();
  const { localeContentWarnings, localeContentErrors } = useErrorValidationContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const [headerType, setHeaderType] = useState(HeaderType.None);
  const [previousVal, setPreviousVal] = useState({
    mediaUrl: '',
    text: '',
  });
  const initialHeaderData = ((initialTemplate?.mediumData || {}) as WhatsAppContentDefinition)
    .header;
  const value =
    (whatsAppData.header as TextHeader)?.text ||
    (whatsAppData.header as ImageHeader)?.mediaUrl ||
    '';
  const { metaApprovalStatus } = whatsAppData;
  const isImgHeaderEditableByStatus = !!(
    metaApprovalStatus && BI_IMAGE_HEADER_EDITABLE_STATUSES.includes(metaApprovalStatus)
  );

  useEffect(() => {
    setPreviousVal({ mediaUrl: '', text: '', ...whatsAppData.header });
  }, [initialTemplate]);

  useEffect(() => {
    if ((whatsAppData.header as TextHeader)?.text !== undefined) {
      setHeaderType(HeaderType.Text);
      setPreviousVal({ ...previousVal, text: value });
    } else if ((whatsAppData.header as ImageHeader)?.mediaUrl !== undefined) {
      setHeaderType(HeaderType.Image);
      setPreviousVal({ ...previousVal, mediaUrl: value });
    } else {
      setHeaderType(HeaderType.None);
    }
  }, [value]);

  const { hasError: hasHeaderError, validationError: headerValidationError } = constructFieldErrors(
    {
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.HEADER,
    },
  );
  const whatsAppHeaderError = constructErrorInlineText({
    condition: isEditMode,
    hasError: hasHeaderError,
    validationError: headerValidationError,
  });

  const handleMediaUrlChange = (newImageUrl: string) => {
    setCurrentMediumField(header, { mediaUrl: newImageUrl });
  };

  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const isHelpTextVisible =
    isEditModeVisible &&
    (isEditableWhatsAppTemplate ||
      (metaApprovalStatus === MetaApprovalStatusDefinition.Approved &&
        headerType === HeaderType.Image));

  const ReadOnlyValue = () => (
    <SettingsReadOnlyText data-test="header-ro" $hasError={hasHeaderError}>
      {hasHeaderError && <StyledNegativeFill color={colors.red100} />}
      {value}
    </SettingsReadOnlyText>
  );

  if (isReviewTemplateTakeover && !value) return null;

  return (
    <StyledFormField
      data-test="whats-app-header-field"
      isHorizontalLayout
      helpText={!hasHeaderError ? getHelpText(isHelpTextVisible, headerType) : null}
      restrictionText={
        headerType === HeaderType.Text ? (
          <WhatsAppRestrictionText
            maxChars={MAX_HEADER_TEXT_LENGTH}
            textContent={(whatsAppData.header as TextHeader)?.text || ''}
          />
        ) : null
      }
      labelText={SETTINGS_DISPLAY_CONTENT.header}
      hasError={hasHeaderError}
      errorText={whatsAppHeaderError}
    >
      {isEditModeVisible ? (
        <>
          {isEditableWhatsAppTemplate && (
            <StyledButtonGroup
              trackSelected
              id="header-group"
              defaultSelectedIds={[headerType]}
              onSelect={(selectedIds) => {
                const currentHeaderType = (selectedIds as HeaderType[])[0];
                setHeaderType(currentHeaderType);
                if (currentHeaderType === HeaderType.Text) {
                  setCurrentMediumField(header, {
                    text: previousVal?.text || '',
                  });
                } else if (currentHeaderType === HeaderType.Image) {
                  setCurrentMediumField(header, {
                    mediaUrl: previousVal?.mediaUrl || '',
                  });
                } else {
                  setCurrentMediumField(header, {});
                }
              }}
            >
              <StyledGroupButton id={HeaderType.None}>None</StyledGroupButton>
              <StyledGroupButton id={HeaderType.Text}>Text</StyledGroupButton>
              <StyledGroupButton id={HeaderType.Image}>Image</StyledGroupButton>
            </StyledButtonGroup>
          )}
          {headerType === HeaderType.Text &&
            (isEditableWhatsAppTemplate ? (
              <InputTextField
                hasError={hasHeaderError && !!(initialHeaderData as TextHeader)?.text}
                label="text"
                value={(whatsAppData.header as TextHeader)?.text || ''}
                setValue={(val) => setCurrentMediumField(header, { text: val })}
              />
            ) : (
              <ReadOnlyValue />
            ))}
          {headerType === HeaderType.Image &&
            (isEditableWhatsAppTemplate || isImgHeaderEditableByStatus ? (
              <UrlInputField
                id={header}
                hasError={hasHeaderError && !!(whatsAppData.header as ImageHeader)?.mediaUrl}
                inputValue={(whatsAppData.header as ImageHeader)?.mediaUrl}
                onChangeHandler={handleMediaUrlChange}
                placeholderText={displayContent.placeholderMediaUrl}
                mediumDataKey={headerMedia}
              />
            ) : (
              <ReadOnlyValue />
            ))}
        </>
      ) : (
        <>
          {value ? (
            <ReadOnlyValue />
          ) : (
            <ReadOnlyField>
              <SettingsReadOnlyText isUnset={true}>
                {SETTINGS_DISPLAY_CONTENT.unsetText}
              </SettingsReadOnlyText>
            </ReadOnlyField>
          )}
        </>
      )}
    </StyledFormField>
  );
};
